import { inject, Injectable } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import isEqual from 'fast-deep-equal';
import {
  combineLatest,
  concatMap,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  Observable,
  take,
} from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  CanvasElement,
  CanvasElementId,
  EmitsClick,
  FIELD_INFO_TYPE_COMMIT,
} from '@blaze-runtime/engine-domain';
import {
  FormState,
  FormStateContainer,
  FormValidationErrors,
  RenderElementContext,
} from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { countedShareReplay, isNullishOrEmptyString } from '@blaze-shared/shared-util';

import { getElementEntities, getElements } from '../../../+state/elements/elements.selectors';
import {
  getActiveInputDataSource,
  getFormlikeBinding,
} from '../../../canvas-elements/element-model/data-binding';
import {
  hasDataProps,
  hasWrapperRefs,
} from '../../../canvas-elements/element-model/element-props-guards';
import {
  isEditableForm,
  isFormList,
} from '../../../canvas-elements/element-model/element-type-guards';
import { FormElement } from '../../../elements/form-container/form-container.model';

import { FormCommitButtonRegistry } from './form-commit-button-registry.service';
import { shouldFormBeValidated } from './form-validation-model';

/** The validity of one field of a formlike container, as its element needs to render it. */
export type FormlikeFieldValidityObservable = Observable<{
  containerId: string;
  submitted: boolean | undefined;
  errors?: FormValidationErrors;
}>;

/** The validity of a formlike container, as its commit button needs to render it. */
export type FormlikeValidity = {
  containerId: string;
  state: FormState<FormStateContainer>;
  containerType: 'formContainer' | 'editableList';
};

/**
 * Streams the validity a running app's form elements render: the container's own validity
 * for its commit button, and each bound field's errors for the field itself.
 */
@Injectable({ providedIn: 'root' })
export class FormValidityService {
  private readonly commitButtons = inject(FormCommitButtonRegistry);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store);

  /**
   * Finds the formlike container a button commits and streams that container's validity.
   * Registers the button on the way, so the form can be submitted from a field as well.
   */
  observeFormlikeValidity(
    buttonId: CanvasElementId,
    commitButton: EmitsClick,
  ): Observable<FormlikeValidity> {
    return this.store.select(runtimeSelectors.selectIsRunning).pipe(
      filter((isRunning) => isRunning),
      concatLatestFrom(() => this.store.select(getElements)),
      map(([, elements]) => {
        const container = elements.find(
          (element) =>
            ((isEditableForm(element) && shouldFormBeValidated(element as FormElement)) ||
              isFormList(element)) &&
            hasWrapperRefs(element) &&
            element.props.wrapper.refs[FIELD_INFO_TYPE_COMMIT] === buttonId,
        );
        if (!container || !hasDataProps(container)) {
          return undefined;
        }

        this.commitButtons.register(container.id, commitButton);

        return { containerId: container.id, containerType: container.type };
      }),
      take(1),
      filter(Boolean),
      concatMap(({ containerId, containerType }) =>
        this.store.select(runtimeSelectors.selectFormStateById(containerId)).pipe(
          map((state) => ({ containerId, containerType, state })),
          distinctUntilChanged(isEqual),
          countedShareReplay(),
        ),
      ),
    );
  }

  /** Streams a form list's own errors — the ones about the list, not about a row. */
  observeFormListValidity(element: CanvasElement): FormlikeFieldValidityObservable {
    if (!getActiveInputDataSource(element)) {
      return EMPTY;
    }

    const containerId = element.id;
    return combineLatest([
      this.store.select(runtimeSelectors.selectFormStateByIdAndRowId(containerId, containerId)),
      this.store.select(runtimeSelectors.selectFormIsSubmittedById(containerId)),
    ]).pipe(
      map(([formState, isFormSubmitted]) => ({
        containerId,
        submitted: isFormSubmitted,
        errors: formState?.errors?.[''] ?? {},
      })),
      distinctUntilChanged(isEqual),
    );
  }

  /**
   * Streams the errors of the field an element is bound to. For a form list the errors are
   * per row, so the element's row context decides which row is observed.
   */
  observeFormlikeFieldValidity(
    element: CanvasElement,
    elementContext$: Observable<RenderElementContext | undefined>,
  ): FormlikeFieldValidityObservable {
    if (!hasDataProps(element)) {
      return EMPTY;
    }

    const containerBinding = getFormlikeBinding(element.props);
    if (!containerBinding?.dataSource) {
      return EMPTY;
    }

    const { sourceType: bindingType, id: containerId } = containerBinding.dataSource;

    return this.store.select(getElementEntities).pipe(
      map((elements) => {
        if (bindingType === 'formContainer') {
          const form = elements[containerId] as FormElement;
          if (!shouldFormBeValidated(form)) {
            return false;
          }
        }

        const elementFieldOrId = element.props.data.find(
          (binding) => binding?.key === 'value' || binding?.key === 'selected',
        )?.dataSource?.name;
        if (!elementFieldOrId) {
          return false;
        }

        return this.schemaService.getColumnName(elementFieldOrId);
      }),
      take(1),
      filter((elementField) => !!elementField),
      concatMap((elementField) => {
        if (typeof elementField !== 'string') {
          return EMPTY;
        }

        switch (bindingType) {
          case 'formContainer':
            return this.store.select(runtimeSelectors.selectFormStateById(containerId)).pipe(
              map((state) => ({
                containerId,
                submitted: state?.submitted,
                errors: state?.errors?.[elementField],
              })),
            );

          case 'editableList':
            return this.observeFormListRowFieldValidity(containerId, elementField, elementContext$);

          default:
            throw new Error(`Binding type "${bindingType}" is not supported`);
        }
      }),
      distinctUntilChanged(isEqual),
    );
  }

  private observeFormListRowFieldValidity(
    containerId: string,
    elementField: string,
    elementContext$: Observable<RenderElementContext | undefined>,
  ): FormlikeFieldValidityObservable {
    return combineLatest({
      formState: this.store.select(runtimeSelectors.selectFormStateById(containerId)),
      elementContext: elementContext$,
    }).pipe(
      take(1),
      concatMap(({ elementContext }) => {
        const rowId = elementContext?.rowContext?.rowId;
        if (isNullishOrEmptyString(rowId)) {
          throw new Error('Form List rowId missing');
        }

        return combineLatest([
          this.store.select(runtimeSelectors.selectFormStateByIdAndRowId(containerId, rowId)),
          this.store.select(runtimeSelectors.selectFormIsSubmittedById(containerId)),
        ]);
      }),
      map(([formState, isFormSubmitted]) => ({
        containerId,
        submitted: isFormSubmitted,
        errors: formState?.errors?.[elementField] ?? {},
      })),
    );
  }
}
